import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FaBell, FaTimes } from 'react-icons/fa';
import { post } from '../../services/back.service';
import { dismissPush, markRead, openDetail } from '../../reducers/notificationsSlice';

const AUTO_CIERRE_MS = 6000;

/**
 * Modal tipo push notification de la pantalla inicial.
 *
 * Muestra el primer elemento de `state.notifications.queue` (lo llenan los
 * eventos en vivo `notification.new`). Se cierra solo pasados unos segundos
 * o con la X —en ninguno de los dos casos se marca como leído—; solo el clic
 * del usuario lo marca y cierra, y después navega a su `link` (agendas) o
 * abre el modal de detalle (anuncios).
 *
 * Se monta una sola vez en la raíz (ver `App.js`), dentro del router.
 */
export default function PushNotificationModal() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const actual = useSelector((state) => state.notifications.queue[0]);

  // Un push a la vez: al sacarlo de la cola (clic, X o tiempo) aparece el
  // siguiente con su propio temporizador.
  useEffect(() => {
    if (!actual) return undefined;
    const timer = setTimeout(() => dispatch(dismissPush()), AUTO_CIERRE_MS);
    return () => clearTimeout(timer);
  }, [actual, dispatch]);

  if (!actual) return null;

  const abrir = () => {
    dispatch(markRead({ id: actual.id, readAt: new Date().toISOString() }));
    dispatch(dismissPush());
    if (actual.kind === 'announcement') {
      // Los anuncios de administrador no navegan: muestran todo su detalle.
      dispatch(openDetail(actual));
    } else if (actual.link) {
      navigate(actual.link);
    }
    post(`/notify/${actual.id}/read`).catch(() => {});
  };

  const descartar = () => dispatch(dismissPush());

  return createPortal(
    <div className="push-overlay">
      <div
        className="push-card"
        role="alertdialog"
        aria-label={actual.title}
        onClick={abrir}
      >
        <div className="push-icon">
          <FaBell />
        </div>
        <div className="push-body">
          <strong className="push-title">{actual.title}</strong>
          <p className="push-message">{actual.message}</p>
        </div>
        <button
          type="button"
          className="push-close"
          aria-label="Cerrar"
          onClick={(event) => {
            event.stopPropagation();
            descartar();
          }}
        >
          <FaTimes />
        </button>
      </div>
    </div>,
    document.body
  );
}
